/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * SignUpForm Component
 * --------
 * A reusable user registration form component designed for signing up new users.
 *
 * Props:
 *  - onSubmit: Function to handle form submission, receives form data and setErrors callback.
 *
 * Features:
 *  - Manages comprehensive form state including first name, last name, username, email, password, and password confirmation.
 *  - Performs client-side validation with real-time feedback on fields, including:
 *     - Required fields validation for first name, username, email, and password.
 *     - Password strength requirements (minimum length, uppercase, special character, number).
 *     - Confirm password matching validation.
 *  - Error messages appear below respective fields and auto-clear after a delay for better UX.
 *  - Integrates with react-router's useNavigate for navigation on back button and sign-in redirect link.
 *  - Pinned to the Agni palette, like SignInForm, and styled with the sign-in
 *    page's `sif-` classes plus SignUpForm.css for the sign-up-only pieces.
 *  - Renders the 'Race Bib' layout: a race bib beside the form that fills in
 *    with the athlete's name and username as they type. The bib number is a
 *    preview worked out from the username; it is not stored anywhere.
 *  - Layout is responsive with accessible form controls and labels.
 *
 * Usage:
 *  <SignUpForm onSubmit={handleSignUp} />
 *
 * Notes:
 *  - The onSubmit function must handle server-side registration and report errors via setErrors.
 *  - Shows a "Back" button that navigates to the home page.
 *  - Includes links for users to navigate to sign-in if they already have an account.
 */


import React, { useState, useEffect, useRef, useMemo } from 'react';
// import { useTheme } from '../theme/ThemeContext'; — the Race Bib layout is pinned to agniTheme, like SignInForm
import envConfig from '../config/envConfig';
import { useAppBranding } from '../hooks/useAppBranding';
// import Button from './Button'; — the old layout's back and submit buttons; see the commented block below
// import VelLogo from './VelLogo';
import BrandMark from './brand/BrandMark';
import GoogleIcon from './GoogleIcon';
import { agniTheme } from '../theme/themes';
import '../styles/SignInForm.css';
import '../styles/SignUpForm.css';
import APIHelper from '../api/APIHelper';
import { getProviderMeta, loadSsoProviders } from '../config/ssoProviders';
import { useNavigate } from 'react-router-dom';

import { withNext, stashSsoNext } from "../utils/returnTo";
import { DEFAULT_DIAL_CODE, dialCodeEntry, normaliseNumber, validateNumber } from './athlohive/event-detail/countryDialCodes';
import {
  NAME_MAX_LEN, USERNAME_MAX_LEN, EMAIL_MAX_LEN, PASSWORD_MAX_LEN,
  sanitizePersonName, validatePersonName,
} from '../utils/authFieldRules';
const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/**
 * buildSsoAuthorizeUrl
 * --------
 * Pure function (no component state, no window.location reads beyond
 * `window.location.origin` for the redirect_uri) that builds the full-page
 * redirect URL to the backend's SsoAuthorizeAPIView (D-01).
 *
 * Duplicated verbatim from SignInForm.js — zero dependencies on component
 * state, so a tiny duplication is acceptable here over introducing a new
 * shared module for a single 6-line pure function (executor's choice,
 * documented in SUMMARY per plan's discretion note).
 */
// Previous version — one redirect_uri shape for every provider. Correct for
// Google, but Apple never arrives this way; see FORM_POST_PROVIDERS below.
// export function buildSsoAuthorizeUrl({ backendApiUrl, providerKey, intent, applicationId }) {
//   const redirectUri = `${window.location.origin}/auth/sso/callback/${providerKey}`;
//   const params = new URLSearchParams({
//     intent,
//     application_id: applicationId,
//     redirect_uri: redirectUri,
//   });
//   return `${backendApiUrl}/arcee/sso/${providerKey}/authorize/?${params.toString()}`;
// }

/**
 * Providers that answer with response_mode=form_post instead of a GET redirect.
 *
 * Apple requires form_post whenever name/email is requested, which means it
 * HTTP POSTs `state`/`code` to the Return URL. This app is a static SPA: a POST
 * to one of its routes never reaches React, and the values sit in a body no
 * client-side code can read. So Apple's registered Return URL is the backend's
 * bounce endpoint, and `return_to` names the page to land on afterwards —
 * SsoFormPostBounceAPIView 302s there with ?state&code, which SsoCallbackPage
 * already consumes provider-agnostically.
 */
const FORM_POST_PROVIDERS = ['apple'];

export function buildSsoAuthorizeUrl({ backendApiUrl, providerKey, intent, applicationId }) {
  const callbackUri = `${window.location.origin}/auth/sso/callback/${providerKey}`;
  const isFormPost = FORM_POST_PROVIDERS.includes(providerKey);
  const params = new URLSearchParams({
    intent,
    application_id: applicationId,
    redirect_uri: isFormPost
      ? `${backendApiUrl}/arcee/sso/${providerKey}/form-post/`
      : callbackUri,
  });
  // Both values are allowlisted server-side (SsoProviderConfig.redirect_uri_allowlist);
  // an unlisted one is a 400 from the authorize view, never a silent fallback.
  if (isFormPost) params.set('return_to', callbackUri);
  return `${backendApiUrl}/arcee/sso/${providerKey}/authorize/?${params.toString()}`;
}

/**
 * bibNumberFor
 * --------
 * The four-digit number printed on the sign-up bib. Worked out from the
 * username so the same username always shows the same number, and 2417 while
 * the field is empty. Display only: it is never sent to the server, and two
 * usernames can share a number.
 */
export function bibNumberFor(username) {
  const u = (username || '').trim().toLowerCase();
  if (!u) return '2417';
  let h = 7;
  for (let i = 0; i < u.length; i += 1) h = (h * 31 + u.charCodeAt(i)) >>> 0;
  return String(1000 + (h % 8999));
}

function SignUpForm({ onSubmit }) {
  // const { theme } = useTheme(); — replaced by the pinned Agni palette below
  const theme = agniTheme;
  // const { data: branding } = useAppBranding();
  const { data: branding, isPending: brandingPending } = useAppBranding();
  const appName = branding?.app_name || envConfig.appName;
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const errorTimeouts = useRef({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [ssoProviders, setSsoProviders] = useState([]);

  useEffect(() => {
    let mounted = true;
    // Shares the cached list with SignInForm — see config/ssoProviders.
    // The inline fetch this replaced is kept below for reference.
    //
    // (async () => {
    //   try {
    //     const resp = await apiHelper.apiCall('/arcee/sso/providers/', { method: 'GET' });
    //     if (!resp.ok) return;
    //     const data = await resp.json();
    //     if (mounted) setSsoProviders(Array.isArray(data) ? data : []);
    //   } catch (_err) {
    //     // Network/auth error — fail open, simply don't show SSO buttons.
    //   }
    // })();
    loadSsoProviders(apiHelper).then((list) => {
      if (mounted) setSsoProviders(list);
    });
    return () => { mounted = false; };
  }, []);

  const handleSsoClick = (providerKey) => {
    // Same reason as SignInForm: ?next= cannot survive the authorize round trip
    // in the URL, so it waits in sessionStorage until SsoCallbackPage lands.
    stashSsoNext();

    window.location = buildSsoAuthorizeUrl({
      backendApiUrl: envConfig.backendApiUrl,
      providerKey,
      intent: 'signup',
      applicationId: envConfig.applicationId,
    });
  };

  const clearErrorAfterDelay = (field, delay = 4000) => {
    if (errorTimeouts.current[field]) clearTimeout(errorTimeouts.current[field]);
    errorTimeouts.current[field] = setTimeout(() => {
      setErrors(prev => ({ ...prev, [field]: '' }));
      delete errorTimeouts.current[field];
    }, delay);
  };

  // Password pattern: at least 1 uppercase letter, 1 special char, 1 number.
  // The special characters are exactly the server's set (validate_password in
  // serializers.py): [\W_] also let through "-", "_" or "+", which the server
  // then rejected after the form said the password was fine.
  // const passwordPattern = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/;
  const passwordPattern = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).+$/;
  const PASSWORD_RULE = 'Password must contain an uppercase letter, a number and a special character (!@#$%^&* etc.).';
  // Same rules the server applies, so a bad value is caught before the request.
  const usernamePattern = new RegExp(`^[A-Za-z0-9_]{1,${USERNAME_MAX_LEN}}$`);
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Country code is fixed to India for now; the field takes the national number only.
  const dialCode = DEFAULT_DIAL_CODE;
  const dial = dialCodeEntry(dialCode);

  const handleChange = (e) => {
    const { name } = e.target;
    // Mobile: digits only, never longer than an Indian number.
    // Names: numbers and symbols are dropped as they are typed or pasted.
    let value = e.target.value;
    if (name === 'mobile') value = normaliseNumber(value).slice(0, 10);
    else if (name === 'firstName' || name === 'lastName') value = sanitizePersonName(value).slice(0, NAME_MAX_LEN);
    setForm(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear existing error
    setErrors(prev => ({
      ...prev,
      [name]: '',
    }));

    // Real-time password validation on password field
    // Rules are checked against the TRIMMED value, since that is what
    // handleSubmit sends — a trailing space must not count towards the length.
    if (name === 'password') {
      const pw = value.trim();
      if (pw.length < 8) {
        setErrors(prev => ({ ...prev, password: 'Password must be at least 8 characters.' }));
      } else if (!passwordPattern.test(pw)) {
        setErrors(prev => ({
          ...prev,
          // password: 'Password must contain uppercase, special character, and number.',
          password: PASSWORD_RULE,
        }));
      } else {
        // Clear password error if valid
        setErrors(prev => ({ ...prev, password: '' }));
      }
      // Editing the password after the confirmation was typed must re-check the match.
      if (form.confirmPassword) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: form.confirmPassword.trim() !== pw ? 'Passwords do not match.' : '',
        }));
      }
    }

    // Confirm password real-time check
    if (name === 'confirmPassword') {
      if (value.trim() !== form.password.trim()) {
        setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match.' }));
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }

    // Mobile: flag it once the number is complete (or starts with 0), not on
    // every keystroke while it is still being typed.
    if (name === 'mobile' && (value.length === 10 || value.startsWith('0'))) {
      const check = validateNumber(dialCode, value);
      if (!check.valid) setErrors(prev => ({ ...prev, mobile: check.reason }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Trim leading/trailing whitespace so accidental spaces (e.g. from autofill
    // or a trailing keyboard space) aren't submitted. Internal spaces are kept.
    const trimmed = {
      ...form,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      username: form.username.trim(),
      email: form.email.trim(),
      password: form.password.trim(),
      confirmPassword: form.confirmPassword.trim(),
    };
    let errs = {};
    const firstNameError = validatePersonName(trimmed.firstName, 'First name');
    if (firstNameError) errs.firstName = firstNameError;
    // Last name stays optional, but when given it follows the same rules.
    const lastNameError = validatePersonName(trimmed.lastName, 'Last name', { required: false });
    if (lastNameError) errs.lastName = lastNameError;
    if (!trimmed.username) errs.username = "Username is required.";
    else if (!usernamePattern.test(trimmed.username))
      errs.username = `Use letters, numbers and underscores only, up to ${USERNAME_MAX_LEN} characters.`;
    if (!trimmed.email) errs.email = "Email is required.";
    else if (trimmed.email.length > EMAIL_MAX_LEN) errs.email = `Email must be at most ${EMAIL_MAX_LEN} characters.`;
    else if (!emailPattern.test(trimmed.email)) errs.email = "Enter a valid email address.";
    const mobileCheck = validateNumber(dialCode, form.mobile);
    if (!mobileCheck.valid) errs.mobile = mobileCheck.reason;
    if (!trimmed.password) errs.password = "Password is required.";
    else if (trimmed.password.length < 8) errs.password = "Password must be at least 8 characters.";
    else if (trimmed.password.length > PASSWORD_MAX_LEN)
      errs.password = `Password must be at most ${PASSWORD_MAX_LEN} characters.`;
    // if (!passwordPattern.test(form.password))
    //   errs.password = "Password must contain uppercase, special character, and number.";
    else if (!passwordPattern.test(trimmed.password)) errs.password = PASSWORD_RULE;
    if (!trimmed.confirmPassword) errs.confirmPassword = "Please confirm your password.";
    else if (trimmed.confirmPassword !== trimmed.password) errs.confirmPassword = "Passwords do not match.";
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      for (const field of Object.keys(errs)) {
        clearErrorAfterDelay(field);
      }
      return;
    }

    setLoading(true);
    try {
      await onSubmit(trimmed, setErrors);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Object.keys(errors).forEach(field => {
      if (errors[field]) {
        clearErrorAfterDelay(field);
      }
    });
    return () => {
      Object.values(errorTimeouts.current).forEach(clearTimeout);
    };
  }, [errors]);

  // ── Race Bib layout ──────────────────────────────────────
  const hexAlpha = (hex, aa) =>
    (/^#[0-9a-fA-F]{6}$/.test(hex || '') ? `${hex}${aa}` : hex);
  const primary = theme?.primary || '#ff7a00';
  const secondary = theme?.secondary || theme?.primaryLight || primary;
  const themeVars = {
    '--sif-p': primary,
    '--sif-s': secondary,
    '--sif-p08': hexAlpha(primary, '14'),
    '--sif-p14': hexAlpha(primary, '24'),
    '--sif-p30': hexAlpha(primary, '4d'),
    '--sif-tint': hexAlpha(primary, '12'),
    '--sif-radius': theme?.buttonRadius || '0.5rem',
  };

  const bibNumber = useMemo(() => bibNumberFor(form.username), [form.username]);
  const bibName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
  const bibHandle = form.username.trim();

  // Strength meter: one bar per server rule (length, uppercase, number, symbol),
  // so a full meter means the password will be accepted.
  // Scored on the trimmed password, matching what handleSubmit sends.
  const trimmedPassword = form.password.trim();
  const passwordLevel = trimmedPassword
    ? [
        trimmedPassword.length >= 8,
        /[A-Z]/.test(trimmedPassword),
        /\d/.test(trimmedPassword),
        /[!@#$%^&*(),.?":{}|<>]/.test(trimmedPassword),
      ].filter(Boolean).length
    : 0;
  const passwordWord = ['', 'Too weak', 'Weak', 'Fair', 'Strong'][passwordLevel];

  return (
    <div className="sif-shell suf-shell" style={themeVars}>
      <div className="sif-halo" aria-hidden="true" />

      <div className="sif-grid">
        {/* ── Left: the race bib ────────────────────────────────
            Fills in live from the form. Display only — nothing here is sent. */}
        <div className="suf-left">
          <span className="sif-club"><i>26</i> Registration desk &middot; 2026 season</span>

          <h2 className="sif-title">Your number is <em>waiting.</em></h2>
          <p className="sif-sub">
            Create your account to get on the start list, join club runs and log every session you train.
          </p>

          <div className="suf-bibwrap">
            <div className="suf-bib" role="img" aria-label={`Race bib preview: ${bibNumber}, ${bibName || 'your name'}`}>
              <div className="suf-bib-top" aria-hidden="true">
                <span>{appName} athlete</span>
                <svg viewBox="0 0 32 32">
                  <path d="M16 2.5l11.7 6.75v13.5L16 29.5 4.3 22.75V9.25z" fill="#fff" />
                  <path d="M10 19.5l4-7 3 4.5 2-3 3 5.5" fill="none" stroke={primary} strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Season 2026</span>
              </div>
              <div className="suf-bib-body" aria-hidden="true">
                <div className="suf-bib-no" data-testid="bib-number">{bibNumber}</div>
                <div className="suf-bib-name" data-testid="bib-name">{bibName || 'Your name'}</div>
                <div className="suf-bib-handle">@{bibHandle || 'username'}</div>
              </div>
              <div className="suf-bib-foot" aria-hidden="true">
                <div className="suf-bib-bars" />
                <div className="suf-bib-chip">Timing chip<br /><b>AH-{bibNumber}-OPEN</b></div>
              </div>
              <i className="suf-pin suf-pin-a" /><i className="suf-pin suf-pin-b" />
              <i className="suf-pin suf-pin-c" /><i className="suf-pin suf-pin-d" />
            </div>
          </div>

          <ul className="suf-perks">
            <li><i className="bi bi-check2" aria-hidden="true" />Log runs, rides and swims</li>
            <li><i className="bi bi-check2" aria-hidden="true" />Register for club events</li>
            <li><i className="bi bi-check2" aria-hidden="true" />Get timed results</li>
          </ul>
        </div>

        {/* ── Right: the registration card ───────────────────── */}
        <div className="sif-card">
          <div className="sif-card-head">
            <button
              type="button"
              className="sif-back"
              onClick={() => navigate('/')}
              aria-label="Back to home"
            >
              <i className="bi bi-arrow-left" />
            </button>
            {/* The homepage's lockup (logo + Athlo/Hive wordmark), same as the
                sign-in card. While branding loads a placeholder shows instead of
                envConfig's fallback name. */}
            <div className="sif-brand">
              <BrandMark name={appName} logo={branding?.logo} pending={brandingPending} />
            </div>
          </div>

          <h1 className="sif-h1">Create account</h1>

          <form onSubmit={handleSubmit} className="sif-form" noValidate>
            <div className="suf-row">
              <div>
                <label htmlFor="firstName" className="sif-label">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className="sif-input"
                  placeholder="Priya"
                  value={form.firstName}
                  onChange={handleChange}
                  maxLength={NAME_MAX_LEN}
                  autoComplete="given-name"
                />
                {errors.firstName && <div className="sif-error">{errors.firstName}</div>}
              </div>
              <div>
                <label htmlFor="lastName" className="sif-label">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className="sif-input"
                  placeholder="Sharma"
                  value={form.lastName}
                  onChange={handleChange}
                  maxLength={NAME_MAX_LEN}
                  autoComplete="family-name"
                />
                {errors.lastName && <div className="sif-error">{errors.lastName}</div>}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="sif-label">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                className="sif-input"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                maxLength={EMAIL_MAX_LEN}
                autoComplete="email"
              />
              {errors.email && <div className="sif-error">{errors.email}</div>}
            </div>

            {/* Username + mobile: both short values, so they share a row on
                wider screens. Email stays full width for long addresses. */}
            <div className="suf-row suf-row-md">
              <div>
                <label htmlFor="username" className="sif-label">Username</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  className="sif-input"
                  placeholder="priya_runs"
                  value={form.username}
                  onChange={handleChange}
                  maxLength={USERNAME_MAX_LEN}
                  autoComplete="username"
                  autoCapitalize="off"
                  spellCheck={false}
                />
                {errors.username && <div className="sif-error">{errors.username}</div>}
              </div>

              {/* Mobile — country code fixed to +91 for now */}
              <div>
                <label htmlFor="mobile" className="sif-label">Mobile Number</label>
                <div className="suf-tel">
                  <span className="suf-code" aria-label={`Country code ${dialCode}, ${dial.name}`}>
                    {dialCode}
                  </span>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    className="sif-input"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder={dial.sample}
                    value={form.mobile}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.mobile)}
                    aria-describedby="mobile-hint"
                    autoComplete="tel-national"
                  />
                </div>
                {errors.mobile ? (
                  <div id="mobile-hint" role="alert" className="sif-error">{errors.mobile}</div>
                ) : (
                  <div id="mobile-hint" className="suf-hint">{dial.hint}</div>
                )}
              </div>
            </div>

            {/* Password + confirm share a row on wider screens. The strength
                meter and the (long) password rule message sit under both,
                full width, so neither is squeezed into a half column. */}
            <div>
              <div className="suf-row suf-row-md">
              <div>
                <label htmlFor="password" className="sif-label">Password</label>
                {/* The toggle is positioned against the input itself, not the whole
                    field, so it stays put when an error message appears below. */}
                <div className="sif-inputwrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    className="sif-input no-edge-eye"
                    placeholder="8+ characters"
                    value={form.password}
                    onChange={handleChange}
                    maxLength={PASSWORD_MAX_LEN}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="sif-eye"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'} />
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="sif-label">Confirm Password</label>
                <div className="sif-inputwrap">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    className="sif-input no-edge-eye"
                    placeholder="Repeat it"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    maxLength={PASSWORD_MAX_LEN}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="sif-eye"
                    onClick={() => setShowConfirmPassword(v => !v)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    <i className={showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'} />
                  </button>
                </div>
                {errors.confirmPassword && <div className="sif-error">{errors.confirmPassword}</div>}
              </div>
              </div>
              <div className="suf-meter" data-level={passwordLevel} aria-hidden="true">
                <i /><i /><i /><i /><span>{passwordWord}</span>
              </div>
              {errors.password && <div className="sif-error">{errors.password}</div>}
            </div>

            <button type="submit" className="sif-cta" disabled={loading}>
              {loading ? 'Signing Up...' : 'Sign Up'}
            </button>
            <p className="suf-terms">By creating an account you agree to the Terms and Privacy Policy.</p>
          </form>

          {/* SSO button row (D-08, D-09, D-10) — below the Sign Up button, and
              suppressed entirely when no active providers */}
          {ssoProviders.length > 0 && (
            <>
              <div className="sif-or">or continue with</div>
              <div className="sif-sso-group">
                {ssoProviders.map((p) => {
                  const meta = getProviderMeta(p.provider_key);
                  return (
                    <button
                      key={p.provider_key}
                      type="button"
                      className="sif-sso"
                      onClick={() => handleSsoClick(p.provider_key)}
                    >
                      {p.provider_key === 'google' ? (
                        <GoogleIcon style={{ marginRight: 0 }} />
                      ) : (
                        <i className={`bi ${meta.icon}`} />
                      )}
                      Continue with {meta.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <p className="sif-meta">
            Already have an account?{' '}
            <button
              type="button"
              className="sif-link"
              onClick={() => navigate(withNext('/sign-in'))}
            >
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );

  // ── Previous layout (heart-grid card), replaced by the Race Bib layout above ──
  // // Heart SVG path for background mask
  // const heartPath = "M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z";
  //
  // return (
  //   <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center" style={{ background: '#f8f9fa', position: 'relative', overflow: 'hidden' }}>
  //
  //     {/* Heart Grid Background */}
  //     <div
  //       style={{
  //         position: 'absolute',
  //         top: '50%',
  //         left: '50%',
  //         transform: 'translate(-50%, -50%)',
  //         width: '900px', // Slightly larger for sign up
  //         height: '900px',
  //         backgroundImage: `
  //               linear-gradient(to right, ${theme.primary}40 1px, transparent 1px),
  //               linear-gradient(to bottom, ${theme.primary}40 1px, transparent 1px)
  //             `,
  //         backgroundSize: '40px 40px',
  //         WebkitMaskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='${heartPath}'/%3E%3C/svg%3E")`,
  //         maskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='${heartPath}'/%3E%3C/svg%3E")`,
  //         WebkitMaskSize: 'contain',
  //         maskSize: 'contain',
  //         WebkitMaskRepeat: 'no-repeat',
  //         maskRepeat: 'no-repeat',
  //         WebkitMaskPosition: 'center',
  //         maskPosition: 'center',
  //         opacity: 1,
  //         zIndex: 0,
  //         pointerEvents: 'none'
  //       }}
  //     />
  //
  //     <div
  //       className="shadow-sm px-4 py-5"
  //       style={{
  //         width: 450,
  //         maxWidth: '95vw',
  //         background: 'rgba(255, 255, 255, 0.95)',
  //         backdropFilter: 'blur(10px)',
  //         position: 'relative',
  //         overflow: 'hidden',
  //         borderRadius: 24,
  //         border: '1px solid rgba(255,255,255,0.4)',
  //         boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
  //         zIndex: 1
  //       }}
  //     >
  //       {/* Top Back Button */}
  //       <Button
  //         mode="bordered"
  //         size="sm"
  //         onClick={() => navigate('/')}
  //         className="position-absolute"
  //         style={{
  //           top: 20,
  //           left: 20,
  //           borderRadius: '50%',
  //           width: 38,
  //           height: 38,
  //           padding: 0,
  //           minWidth: 0
  //         }}
  //       >
  //         <i className="bi bi-arrow-left fs-5" />
  //       </Button>
  //
  //       {/* Logo and App Name */}
  //       {/* Logo and App Name */}
  //       <div className="d-flex flex-column align-items-center mb-4 mt-2">
  //         {/* The homepage's lockup (logo + Athlo/Hive wordmark), same as the
  //             sign-in card. While branding loads a placeholder shows instead of
  //             envConfig's fallback name. */}
  //         <div className="sif-brand">
  //           <BrandMark name={appName} logo={branding?.logo} pending={brandingPending} />
  //         </div>
  //         {/* Previous logo + name, replaced by BrandMark above:
  //         {branding?.logo ? (
  //           <img
  //             src={branding.logo}
  //             alt={envConfig.appName}
  //             style={{
  //               height: 60,
  //               borderRadius: 12,
  //               boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
  //             }}
  //           />
  //         ) : (
  //           <VelLogo size={64} />
  //         )}
  //         <h3
  //           className="mt-3 mb-1"
  //           style={{
  //             color: theme.primary,
  //             fontWeight: 800,
  //             letterSpacing: '-0.5px'
  //           }}
  //         >
  //           {envConfig.appName}
  //         </h3>
  //         */}
  //         <p className="mt-3" style={{ color: '#666', fontSize: '0.95rem', margin: 0 }}>
  //           Create your account to get started.
  //         </p>
  //       </div>
  //
  //       <form onSubmit={handleSubmit} style={{ position: 'relative', zIndex: 1 }}>
  //         <h4 className="mb-4 fw-bold text-center" style={{ display: 'none' }}>
  //           Create Account
  //         </h4>
  //         <p style={{ fontSize: 14, textAlign: 'center', marginBottom: '1.5rem', color: '#666' }}>
  //           Already have an account?{' '}
  //           <span
  //             style={{ color: theme.primary, cursor: 'pointer', textDecoration: 'underline' }}
  //             onClick={() => navigate(withNext('/sign-in'))}
  //           >
  //             Sign In
  //           </span>
  //         </p>
  //
  //         {/* Name Fields */}
  //         <div className="d-flex gap-2 mb-3">
  //           <div style={{ flex: 1 }}>
  //             <label
  //               htmlFor="firstName"
  //               className="form-label fw-semibold"
  //               style={{ color: theme.primary, fontSize: 15 }}
  //             >
  //               First Name
  //             </label>
  //             <input
  //               type="text"
  //               id="firstName"
  //               name="firstName"
  //               className="form-control"
  //               placeholder="Enter your first name"
  //               value={form.firstName}
  //               onChange={handleChange}
  //               style={{
  //                 borderRadius: theme.buttonRadius,
  //                 borderColor: theme.primary + '55'
  //               }}
  //               autoComplete="given-name"
  //             />
  //             {errors.firstName && (
  //               <div style={{ color: "#c80b23", fontSize: 13, marginTop: 4 }}>
  //                 {errors.firstName}
  //               </div>
  //             )}
  //           </div>
  //
  //           <div style={{ flex: 1 }}>
  //             <label
  //               htmlFor="lastName"
  //               className="form-label fw-semibold"
  //               style={{ color: theme.primary, fontSize: 15 }}
  //             >
  //               Last Name
  //             </label>
  //             <input
  //               type="text"
  //               id="lastName"
  //               name="lastName"
  //               className="form-control"
  //               placeholder="Enter your last name"
  //               value={form.lastName}
  //               onChange={handleChange}
  //               style={{
  //                 borderRadius: theme.buttonRadius,
  //                 borderColor: theme.primary + '55'
  //               }}
  //               autoComplete="family-name"
  //             />
  //           </div>
  //         </div>
  //
  //         {/* Username Field */}
  //         <div className="mb-3">
  //           <label
  //             htmlFor="username"
  //             className="form-label fw-semibold"
  //             style={{ color: theme.primary, fontSize: 15 }}
  //           >
  //             Username
  //           </label>
  //           <input
  //             type="text"
  //             id="username"
  //             name="username"
  //             className="form-control"
  //             placeholder="Enter your username"
  //             value={form.username}
  //             onChange={handleChange}
  //             style={{
  //               borderRadius: theme.buttonRadius,
  //               borderColor: theme.primary + '55'
  //             }}
  //             autoComplete="username"
  //           />
  //           {errors.username && (
  //             <div style={{ color: "#c80b23", fontSize: 13, marginTop: 4 }}>
  //               {errors.username}
  //             </div>
  //           )}
  //         </div>
  //
  //         {/* Email Field */}
  //         <div className="mb-3">
  //           <label
  //             htmlFor="email"
  //             className="form-label fw-semibold"
  //             style={{ color: theme.primary, fontSize: 15 }}
  //           >
  //             Email Address
  //           </label>
  //           <input
  //             type="email"
  //             id="email"
  //             name="email"
  //             className="form-control"
  //             placeholder="Enter your email"
  //             value={form.email}
  //             onChange={handleChange}
  //             style={{
  //               borderRadius: theme.buttonRadius,
  //               borderColor: theme.primary + '55'
  //             }}
  //             autoComplete="email"
  //           />
  //           {errors.email && (
  //             <div style={{ color: "#c80b23", fontSize: 13, marginTop: 4 }}>
  //               {errors.email}
  //             </div>
  //           )}
  //         </div>
  //
  //         {/* Mobile Field — country code fixed to +91 for now */}
  //         <div className="mb-3">
  //           <label
  //             htmlFor="mobile"
  //             className="form-label fw-semibold"
  //             style={{ color: theme.primary, fontSize: 15 }}
  //           >
  //             Mobile Number
  //           </label>
  //           <div className="input-group">
  //             <span
  //               className="input-group-text"
  //               aria-label={`Country code ${dialCode}, ${dial.name}`}
  //               style={{
  //                 borderRadius: `${theme.buttonRadius} 0 0 ${theme.buttonRadius}`,
  //                 borderColor: theme.primary + '55',
  //                 fontWeight: 600,
  //               }}
  //             >
  //               {dialCode}
  //             </span>
  //             <input
  //               type="tel"
  //               id="mobile"
  //               name="mobile"
  //               className="form-control"
  //               inputMode="numeric"
  //               maxLength={10}
  //               placeholder={dial.sample}
  //               value={form.mobile}
  //               onChange={handleChange}
  //               aria-invalid={Boolean(errors.mobile)}
  //               aria-describedby="mobile-hint"
  //               style={{
  //                 borderRadius: `0 ${theme.buttonRadius} ${theme.buttonRadius} 0`,
  //                 borderColor: theme.primary + '55'
  //               }}
  //               autoComplete="tel-national"
  //             />
  //           </div>
  //           {errors.mobile ? (
  //             <div id="mobile-hint" role="alert" style={{ color: "#c80b23", fontSize: 13, marginTop: 4 }}>
  //               {errors.mobile}
  //             </div>
  //           ) : (
  //             <div id="mobile-hint" style={{ color: "#6c757d", fontSize: 13, marginTop: 4 }}>
  //               {dial.hint}
  //             </div>
  //           )}
  //         </div>
  //
  //         {/* Password Field */}
  //         <div className="mb-3" style={{ position: "relative" }}>
  //           <label
  //             htmlFor="password"
  //             className="form-label fw-semibold"
  //             style={{ color: theme.primary, fontSize: 15 }}
  //           >
  //             Password
  //           </label>
  //           <input
  //             type={showPassword ? "text" : "password"}
  //             id="password"
  //             name="password"
  //             className="form-control no-edge-eye"
  //             placeholder="Enter your password"
  //             value={form.password}
  //             onChange={handleChange}
  //             style={{
  //               borderRadius: theme.buttonRadius,
  //               borderColor: theme.primary + '55'
  //             }}
  //             autoComplete="new-password"
  //           />
  //           {/* Eye icon */}
  //           <span
  //             style={{
  //               position: "absolute",
  //               right: 12,
  //               top: "40px",
  //               cursor: "pointer",
  //               color: theme.primary
  //             }}
  //             onClick={() => setShowPassword(v => !v)}
  //             title={showPassword ? "Hide password" : "Show password"}
  //           >
  //             <i className={showPassword ? "bi bi-eye-slash" : "bi bi-eye"} />
  //           </span>
  //           {errors.password && (
  //             <div style={{ color: "#c80b23", fontSize: 13, marginTop: 4 }}>
  //               {errors.password}
  //             </div>
  //           )}
  //         </div>
  //
  //         {/* Confirm Password Field */}
  //         <div className="mb-3" style={{ position: "relative" }}>
  //           <label
  //             htmlFor="confirmPassword"
  //             className="form-label fw-semibold"
  //             style={{ color: theme.primary, fontSize: 15 }}
  //           >
  //             Confirm Password
  //           </label>
  //           <input
  //             type={showConfirmPassword ? "text" : "password"}
  //             id="confirmPassword"
  //             name="confirmPassword"
  //             className="form-control no-edge-eye"
  //             placeholder="Confirm your password"
  //             value={form.confirmPassword}
  //             onChange={handleChange}
  //             style={{
  //               borderRadius: theme.buttonRadius,
  //               borderColor: theme.primary + '55'
  //             }}
  //             autoComplete="new-password"
  //           />
  //           {/* Eye icon */}
  //           <span
  //             style={{
  //               position: "absolute",
  //               right: 12,
  //               top: "40px",
  //               cursor: "pointer",
  //               color: theme.primary
  //             }}
  //             onClick={() => setShowConfirmPassword(v => !v)}
  //             title={showConfirmPassword ? "Hide password" : "Show password"}
  //           >
  //             <i className={showConfirmPassword ? "bi bi-eye-slash" : "bi bi-eye"} />
  //           </span>
  //           {errors.confirmPassword && (
  //             <div style={{ color: "#c80b23", fontSize: 13, marginTop: 4 }}>
  //               {errors.confirmPassword}
  //             </div>
  //           )}
  //         </div>
  //
  //         {/* Sign Up Button */}
  //         <div className="d-grid">
  //           <Button
  //             mode="bordered"
  //             type="submit"
  //             disabled={loading}
  //           >
  //             {loading ? 'Signing Up...' : 'Sign Up'}
  //           </Button>
  //         </div>
  //       </form>
  //
  //       {/* SSO button row (D-08, D-09, D-10) — suppressed entirely when no active providers */}
  //       {ssoProviders.length > 0 && (
  //         <>
  //           <div style={{
  //             display: 'flex',
  //             alignItems: 'center',
  //             gap: 12,
  //             margin: '24px 0 16px',
  //           }}>
  //             <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
  //             <span style={{ fontSize: 16, color: '#94a3b8', fontWeight: 400, whiteSpace: 'nowrap' }}>
  //               or continue with
  //             </span>
  //             <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
  //           </div>
  //           {/* Previous themed-Button version, replaced by the sign-in page's
  //               .sif-sso button below so both screens show the same Google button:
  //           <div className="d-grid" style={{ gap: 8 }}>
  //             {ssoProviders.map((p) => {
  //               const meta = getProviderMeta(p.provider_key);
  //               return (
  //                 <Button
  //                   key={p.provider_key}
  //                   mode="outlined"
  //                   onClick={() => handleSsoClick(p.provider_key)}
  //                 >
  //                   <i className={`bi ${meta.icon}`} style={{ marginRight: 8 }} />
  //                   Continue with {meta.label}
  //                 </Button>
  //               );
  //             })}
  //           </div>
  //           */}
  //           {/* Same button as SignInForm: .sif-sso styling with the Agni tokens
  //               SignInForm hands to its stylesheet, not the active theme's colours. */}
  //           <div
  //             className="sif-sso-group"
  //             style={{
  //               '--sif-p': agniTheme.primary,
  //               '--sif-p08': /^#[0-9a-fA-F]{6}$/.test(agniTheme.primary || '')
  //                 ? `${agniTheme.primary}14`
  //                 : agniTheme.primary,
  //               '--sif-radius': agniTheme.buttonRadius || '0.5rem',
  //               position: 'relative',
  //               zIndex: 1,
  //             }}
  //           >
  //             {ssoProviders.map((p) => {
  //               const meta = getProviderMeta(p.provider_key);
  //               return (
  //                 <button
  //                   key={p.provider_key}
  //                   type="button"
  //                   className="sif-sso"
  //                   onClick={() => handleSsoClick(p.provider_key)}
  //                 >
  //                   {p.provider_key === 'google' ? (
  //                     <GoogleIcon style={{ marginRight: 0 }} />
  //                   ) : (
  //                     <i className={`bi ${meta.icon}`} />
  //                   )}
  //                   Continue with {meta.label}
  //                 </button>
  //               );
  //             })}
  //           </div>
  //         </>
  //       )}
  //
  //       {/* Bottom Gradient Bar */}
  //       <div
  //         style={{
  //           height: 8,
  //           width: '100%',
  //           borderBottomLeftRadius: '1.5rem',
  //           borderBottomRightRadius: '1.5rem',
  //           background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`,
  //           position: 'absolute',
  //           left: 0,
  //           bottom: 0,
  //           zIndex: 0
  //         }}
  //       />
  //     </div>
  //   </div>
  // );
}

export default SignUpForm;
